import { Link } from "react-router-dom";
import {
  Briefcase,
  Landmark,
  FileText,
  ArrowRight,
  UserX,
  Euro,
  FileX,
  HeartPulse,
} from "lucide-react";
import Seo from "../components/Seo";
import BookCta from "../components/BookCta";
import BookingSection from "../components/BookingSection";
import ReviewsSection from "../components/ReviewsSection";
import CtaBand from "../components/CtaBand";
import { ARTICLES } from "../data/articles";

const HELP_BLOCKS = [
  {
    icon: Briefcase,
    title: "Diritto del lavoro",
    text: "Licenziamenti, stipendi non pagati, TFR, differenze retributive e altre controversie con il datore di lavoro.",
    link: "/diritto-del-lavoro",
    testId: "help-card-lavoro",
  },
  {
    icon: Landmark,
    title: "Previdenza e INPS",
    text: "Pensioni, invalidità civile, indebiti, prestazioni previdenziali e controversie con l'INPS.",
    link: "/diritto-previdenziale",
    testId: "help-card-previdenza",
  },
  {
    icon: FileText,
    title: "Assistenza legale",
    text: "Analisi della situazione, valutazione delle possibili forme di tutela e assistenza nelle procedure giudiziali e stragiudiziali.",
    link: "/contatti",
    testId: "help-card-assistenza",
  },
];

const PROBLEMS = [
  {
    icon: UserX,
    title: "Hai ricevuto un licenziamento?",
    text: "Verifica tempestivamente la tua situazione e le possibili forme di tutela.",
    link: "/licenziamento",
    testId: "problem-card-licenziamento",
  },
  {
    icon: Euro,
    title: "Non hai ricevuto stipendio o TFR?",
    text: "Valuta gli strumenti disponibili per recuperare le somme che ti spettano.",
    link: "/recupero-crediti-tfr",
    testId: "problem-card-crediti",
  },
  {
    icon: FileX,
    title: "L'INPS ha respinto la tua richiesta?",
    text: "Analizza il provvedimento e valuta le eventuali possibilità di ricorso.",
    link: "/inps-ricorsi",
    testId: "problem-card-inps",
  },
  {
    icon: HeartPulse,
    title: "Hai problemi con pensione o invalidità?",
    text: "Esamina la tua posizione previdenziale e le possibili forme di tutela.",
    link: "/invalidita-civile",
    testId: "problem-card-invalidita",
  },
  {
    icon: Landmark,
    title: "L'INPS ti ha chiesto di restituire delle somme?",
    text: "Analizzo la richiesta ricevuta e valuto la legittimità dell'indebito e le possibili forme di tutela.",
    link: "/indebiti-inps-palermo",
    testId: "problem-card-indebiti",
  },
];

const PILLARS = [
  { n: "01", title: "Ascolto", text: "Comprendere realmente la situazione prima di individuare la strategia." },
  { n: "02", title: "Competenza", text: "Specializzazione nell'ambito del diritto del lavoro e della previdenza." },
  { n: "03", title: "Trasparenza", text: "Spiegare con chiarezza possibilità, rischi e costi dell'azione legale." },
];

const LAVORO_ITEMS = [
  { name: "Licenziamento", link: "/licenziamento" },
  { name: "Impugnazione del licenziamento", link: "/licenziamento" },
  { name: "Recupero crediti da lavoro", link: "/recupero-crediti-tfr" },
  { name: "TFR", link: "/recupero-crediti-tfr" },
  { name: "Differenze retributive", link: "/recupero-crediti-tfr" },
  { name: "Demansionamento", link: "/diritto-del-lavoro" },
  { name: "Mobbing", link: "/diritto-del-lavoro" },
  { name: "Controversie di lavoro", link: "/diritto-del-lavoro" },
];

const PREVIDENZA_ITEMS = [
  { name: "Ricorsi INPS", link: "/inps-ricorsi" },
  { name: "Indebiti previdenziali e assistenziali", link: "/indebiti-inps-palermo" },
  { name: "Richieste di restituzione INPS", link: "/indebiti-inps-palermo" },
  { name: "Invalidità civile", link: "/invalidita-civile" },
  { name: "Ricorso invalidità e accompagnamento", link: "/ricorso-inps-invalidita-accompagnamento-palermo" },
  { name: "Pensioni", link: "/pensioni" },
  { name: "Assegno ordinario di invalidità", link: "/pensioni" },
  { name: "Pensione di inabilità", link: "/pensioni" },
  { name: "Prestazioni previdenziali", link: "/diritto-previdenziale" },
];

const Home = () => (
  <>
    <Seo
      title="Avv. Manfredi Bruno | Avvocato del lavoro e della previdenza a Palermo"
      description="Assistenza legale a Palermo in materia di lavoro, previdenza, pensioni, invalidità civile e rapporti con l'INPS. Prenota un appuntamento con l'Avv. Manfredi Bruno."
      path="/"
    />

    <section data-testid="hero-section" className="relative overflow-hidden bg-navy text-white hero-lines">
      <div className="container-site grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
        <div className="animate-fade-up">
          <p className="kicker text-gold-light">Studio Legale · Palermo</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight">
            Avvocato del lavoro e della previdenza a{" "}
            <em className="text-gold-light not-italic font-semibold">Palermo</em>
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-slate-200">
            Assistenza legale in materia di lavoro, previdenza, pensioni, invalidità
            civile e rapporti con l'INPS.
          </p>
          <p className="mt-4 max-w-xl text-sm sm:text-base leading-relaxed text-slate-400">
            Ogni problema di lavoro o previdenziale richiede una valutazione attenta.
            Analizzo la tua situazione e ti aiuto a comprendere le possibili forme di
            tutela e il percorso più adeguato.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <BookCta testId="hero-cta-book" />
            <a
              href="#come-posso-aiutarti"
              data-testid="hero-cta-secondary"
              className="btn-outline-light"
            >
              Scopri come posso aiutarti <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <div className="absolute -left-3 -top-3 h-full w-full rounded-2xl border border-gold/40" aria-hidden="true" />
          <img
            src="/images/avv-portrait.webp"
            alt="Avv. Manfredi Bruno, avvocato del lavoro e della previdenza, nel suo studio a Palermo"
            data-testid="hero-lawyer-photo"
            className="relative w-full rounded-2xl object-cover shadow-2xl"
            width="900"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute -bottom-5 left-5 rounded-xl bg-white/95 px-5 py-3 shadow-xl backdrop-blur">
            <p className="font-serif text-lg font-semibold text-navy">Avv. Manfredi Bruno</p>
            <p className="text-xs uppercase tracking-[0.15em] text-gold-dark">
              Studio in Via Salvatore Meccio 22
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="come-posso-aiutarti" data-testid="help-section" className="bg-white py-16 sm:py-24">
      <div className="container-site">
        <div className="max-w-2xl">
          <p className="kicker">Come posso aiutarti</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-navy">
            Tre ambiti, un unico obiettivo: tutelare i tuoi diritti
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {HELP_BLOCKS.map((b) => (
            <Link key={b.title} to={b.link} data-testid={b.testId} className="card-elegant group block">
              <b.icon size={30} className="text-gold-dark" aria-hidden="true" />
              <h3 className="mt-5 font-serif text-xl sm:text-2xl font-semibold uppercase tracking-wide text-navy">
                {b.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{b.text}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark transition-transform duration-300 group-hover:translate-x-1">
                Approfondisci <ArrowRight size={15} aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="problems-section" className="bg-slate-50 py-16 sm:py-24">
      <div className="container-site">
        <div className="max-w-2xl">
          <p className="kicker">Hai un problema?</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-navy">
            Partiamo dalla tua situazione concreta
          </h2>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PROBLEMS.map((p) => (
            <Link key={p.title} to={p.link} data-testid={p.testId} className="card-elegant group flex flex-col">
              <p.icon size={28} className="text-gold-dark" aria-hidden="true" />
              <h3 className="mt-4 font-serif text-lg sm:text-xl font-semibold leading-snug text-navy">
                {p.title}
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.text}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark transition-transform duration-300 group-hover:translate-x-1">
                Leggi di più <ArrowRight size={15} aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="why-section" className="bg-white py-16 sm:py-24">
      <div className="container-site grid items-center gap-12 lg:grid-cols-2">
        <div className="relative order-2 lg:order-1">
          <div className="absolute -right-3 -top-3 h-full w-full rounded-2xl border border-gold/40" aria-hidden="true" />
          <img
            src="/images/avv-desk.webp"
            alt="L'Avv. Manfredi Bruno al lavoro presso il suo studio legale a Palermo"
            data-testid="why-lawyer-photo"
            className="relative w-full rounded-2xl object-cover shadow-xl"
            loading="lazy"
            width="1400"
            height="1200"
          />
        </div>
        <div className="order-1 lg:order-2">
          <p className="kicker">Perché scegliere l'Avv. Manfredi Bruno</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-navy">
            Competenza, chiarezza e attenzione alla persona
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Affrontare una controversia di lavoro o un problema previdenziale significa
            spesso trovarsi in una situazione di incertezza. Per questo ritengo
            fondamentale offrire al cliente non soltanto assistenza legale, ma anche
            chiarezza sulle possibilità concrete, sui tempi e sulle strategie percorribili.
          </p>
          <div className="mt-8 space-y-6">
            {PILLARS.map((p) => (
              <div key={p.n} className="flex gap-5" data-testid={`pillar-${p.n}`}>
                <span className="font-serif text-3xl font-semibold text-gold">{p.n}</span>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-navy">{p.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/chi-sono"
            data-testid="why-chi-sono-link"
            className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark transition-transform duration-300 hover:translate-x-1"
          >
            Scopri chi sono <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>

    <section data-testid="areas-section" className="bg-gold-soft py-16 sm:py-24">
      <div className="container-site">
        <div className="max-w-2xl">
          <p className="kicker">Aree di attività</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-navy">
            Due specializzazioni, un'assistenza completa
          </h2>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl bg-navy p-8 text-white shadow-xl" data-testid="area-card-lavoro">
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold uppercase tracking-wide">
              Diritto del lavoro
            </h3>
            <ul className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {LAVORO_ITEMS.map((i) => (
                <li key={i.name}>
                  <Link
                    to={i.link}
                    data-testid={`area-lavoro-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex items-center gap-2 text-sm text-slate-200 transition-colors hover:text-gold-light"
                  >
                    <span className="h-px w-4 bg-gold transition-all duration-300 group-hover:w-6" aria-hidden="true" />
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-gold/30 bg-white p-8 shadow-xl" data-testid="area-card-previdenza">
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold uppercase tracking-wide text-navy">
              Diritto previdenziale
            </h3>
            <ul className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {PREVIDENZA_ITEMS.map((i) => (
                <li key={i.name}>
                  <Link
                    to={i.link}
                    data-testid={`area-previdenza-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex items-center gap-2 text-sm text-slate-700 transition-colors hover:text-gold-dark"
                  >
                    <span className="h-px w-4 bg-gold transition-all duration-300 group-hover:w-6" aria-hidden="true" />
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-10 text-center">
          <Link to="/diritto-del-lavoro" data-testid="areas-cta" className="btn-outline-navy">
            Scopri tutte le aree di attività <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>

    <ReviewsSection />

    <BookingSection />

    <section data-testid="insights-section" className="bg-white py-16 sm:py-24">
      <div className="container-site">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="kicker">Approfondimenti</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-navy">
              Guide chiare su lavoro e previdenza
            </h2>
          </div>
          <Link
            to="/approfondimenti"
            data-testid="insights-all-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark transition-transform duration-300 hover:translate-x-1"
          >
            Tutti gli articoli <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {ARTICLES.slice(0, 3).map((a) => (
            <Link
              key={a.slug}
              to={`/approfondimenti/${a.slug}`}
              data-testid={`article-card-${a.slug}`}
              className="card-elegant group flex flex-col"
            >
              <div className="flex items-center gap-3 text-xs">
                <span className="rounded-full bg-gold-soft px-3 py-1 font-semibold uppercase tracking-wide text-gold-dark">
                  {a.category}
                </span>
                <span className="text-slate-400">{a.readingTime} di lettura</span>
              </div>
              <h3 className="mt-4 font-serif text-xl font-semibold leading-snug text-navy">
                {a.title}
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark transition-transform duration-300 group-hover:translate-x-1">
                Leggi l'articolo <ArrowRight size={15} aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    <CtaBand />
  </>
);

export default Home;
